import React, { useState, useMemo } from 'react';
import { Checkbox, Dropdown, Input, Radio } from 'antd';
import { DownOutlined, UpOutlined, SearchOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { Colors } from '../../theme';
import { PPVFont, textStyles } from '../../theme/fonts-ppv';

type OptionValue = string | number;

export interface RBHMultiSelectOption<T extends OptionValue = string> {
    id: T;
    label: string;
}

export interface RBHMultiSelectProps<T extends OptionValue = string> {
    id?: string;
    label: string;
    options: ReadonlyArray<RBHMultiSelectOption<T>>;
    selectedIds: T[];
    onChange: (ids: T[]) => void;
    style?: React.CSSProperties;
    hideSearch?: boolean;
    hideSelectAll?: boolean;
    mode?: 'select' | 'radio';
}

function RBHMultiSelect<T extends OptionValue>({
    id,
    label,
    options,
    selectedIds,
    onChange,
    style,
    hideSearch = false,
    hideSelectAll = false,
    mode = 'select',
}: RBHMultiSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<T[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const handleVisibleChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setDraft(selectedIds);
            setSearchTerm('');
        }
        setOpen(nextOpen);
    };

    const filteredOptions = useMemo(
        () => options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase())),
        [options, searchTerm]
    );

    const showSelectAll = !hideSelectAll && mode !== 'radio';

    const allSelected =
        filteredOptions.length > 0 && filteredOptions.every((o) => draft.includes(o.id));

    const toggle = (optId: T) => {
        if (mode === 'radio') {
            setDraft([optId]);
        } else {
            setDraft((prev) =>
                prev.includes(optId) ? prev.filter((s) => s !== optId) : [...prev, optId]
            );
        }
    };

    const handleToggleAll = (e: any) => {
        const checked = e.target.checked;
        if (checked) {
            setDraft((prev) =>
                Array.from(new Set([...prev, ...filteredOptions.map((o) => o.id)])) as T[]
            );
        } else {
            const filteredIds = filteredOptions.map((o) => o.id);
            setDraft((prev) => prev.filter((i) => !filteredIds.includes(i)));
        }
    };

    const handleClear = () => setDraft([]);

    const handleConfirm = () => {
        onChange(draft);
        setOpen(false);
    };

    const hasValue = selectedIds.length > 0;

    const overlay = (
        <div style={styles.overlay} onClick={(e) => e.stopPropagation()}>
            {!hideSearch && (
                <div style={styles.searchWrapper}>
                    <FormattedMessage id="main.shared.multiselect.search-placeholder">
                        {(placeholder: any) => (
                            <Input
                                prefix={<SearchOutlined translate={null} style={{ color: Colors.grey400 }} />}
                                placeholder={placeholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        )}
                    </FormattedMessage>
                </div>
            )}

            {showSelectAll && (
                <div style={styles.selectAllWrapper}>
                    <Checkbox checked={allSelected} onChange={handleToggleAll}>
                        <span style={{ ...textStyles.body2, color: Colors.grey600 }}>
                            <FormattedMessage id="main.shared.multiselect.select-all" />
                        </span>
                    </Checkbox>
                </div>
            )}

            <div style={styles.optionList}>
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => (
                        <label key={String(opt.id)} style={styles.optionRow}>
                            {mode === 'radio'
                                ? <Radio checked={draft.includes(opt.id)} onChange={() => toggle(opt.id)} />
                                : <Checkbox checked={draft.includes(opt.id)} onChange={() => toggle(opt.id)} />
                            }
                            <span style={{ ...textStyles.body2, marginLeft: 8 }}>
                                <FormattedMessage id={opt.label} />
                            </span>
                        </label>
                    ))
                ) : (
                    <div style={styles.emptyText}><FormattedMessage id="main.shared.multiselect.no-results" /></div>
                )}
            </div>

            <div style={styles.footer}>
                <button type="button" onClick={handleClear} style={styles.clearButton}>
                    <FormattedMessage id="main.filter.clear" />
                </button>
                <button type="button" onClick={handleConfirm} style={styles.confirmButton}>
                    <FormattedMessage id="main.filter.apply" />
                </button>
            </div>
        </div>
    );

    return (
        <Dropdown
            visible={open}
            onVisibleChange={handleVisibleChange}
            overlay={overlay}
            trigger={['click']}
            placement="bottomLeft"
        >
            <div
                id={id}
                data-testid={id}
                style={{
                    ...styles.trigger,
                    backgroundColor: open ? Colors.grey100 : Colors.grey50,
                    ...style,
                }}
            >
                <div style={styles.triggerLabel}>
                    <span style={{ ...textStyles.subtitle2, color: Colors.grey800 }}>
                        <FormattedMessage id={label} />
                    </span>
                    {hasValue && <span style={styles.badge}>{selectedIds.length}</span>}
                </div>
                {open
                    ? <UpOutlined translate={null} style={styles.chevron} />
                    : <DownOutlined translate={null} style={styles.chevron} />
                }
            </div>
        </Dropdown>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
    trigger: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        minHeight: 42,
        minWidth: 148,
        borderRadius: 8,
        border: `1px solid ${Colors.grey100}`,
        padding: '8px 12px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    } as React.CSSProperties,

    triggerLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    } as React.CSSProperties,

    chevron: {
        fontSize: 12,
        color: Colors.grey400,
    } as React.CSSProperties,

    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 20,
        height: 20,
        borderRadius: 24,
        padding: '0 4px',
        backgroundColor: Colors.grey600,
        ...textStyles.subtitle3,
        color: Colors.white,
    } as React.CSSProperties,

    overlay: {
        backgroundColor: Colors.white,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        minWidth: 160,
        border: `1px solid ${Colors.grey100}`,
        overflow: 'hidden',
    } as React.CSSProperties,

    searchWrapper: {
        padding: '8px 8px 0',
    } as React.CSSProperties,

    selectAllWrapper: {
        padding: '8px 16px 4px',
    } as React.CSSProperties,

    optionList: {
        maxHeight: 240,
        overflowY: 'auto',
        padding: '4px 8px',
    } as React.CSSProperties,

    optionRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '6px 8px',
        cursor: 'pointer',
        borderRadius: 4,
        width: '100%',
    } as React.CSSProperties,

    emptyText: {
        padding: '16px',
        textAlign: 'center' as const,
        ...textStyles.body2,
        color: Colors.grey400,
    } as React.CSSProperties,

    footer: {
        display: 'flex',
        gap: 8,
        padding: 8,
        borderTop: `1px solid ${Colors.grey100}`,
    } as React.CSSProperties,

    clearButton: {
        flex: 1,
        padding: '6px 12px',
        ...textStyles.button2,
        borderRadius: 6,
        border: `1px solid ${Colors.grey200}`,
        color: Colors.grey800,
        backgroundColor: Colors.white,
        cursor: 'pointer',
        fontFamily: PPVFont.Regular,
    } as React.CSSProperties,

    confirmButton: {
        flex: 1,
        padding: '6px 12px',
        ...textStyles.button2,
        borderRadius: 6,
        border: 'none',
        backgroundColor: Colors.violet,
        color: Colors.white,
        cursor: 'pointer',
        fontFamily: PPVFont.Regular,
    } as React.CSSProperties,
};

export default RBHMultiSelect;
